/**
 * @component SoftphoneWidget
 * @description #1422 v2b Stufe 2 — Softphone-Widget in der TopBar (Twilio Voice SDK).
 * Nur sichtbar wenn users.softphone_enabled (Profil-Toggle). Registriert sich als
 * client:ma_<userId> (#1419-Token-Endpoint), zeigt Status (Bereit/Gespräch/offline),
 * eingehende Calls (Annehmen/Ablehnen + Klingelton via SDK, geroutet aufs gewählte
 * Ausgabe-Gerät), im Call Mute + Auflegen. Geräte-Wahl (Mikro + Ausgabe) wird pro
 * User in users.softphone_settings persistiert — Großraum-Regel: Standard bevorzugt
 * Headset-Geräte, erzwingt NIE Lautsprecher.
 *
 * #1424 Stufe 3: Präsenz (available/busy/offline/pause) — Status-Punkt + klickbarer
 * Umschalter; Automatik: registered→available (außer manuell), Call→busy→zurück;
 * 20s-Heartbeat-Poll (Stale-Schutz serverseitig, AK4 ohne Reload).
 *
 * @usage Eingebunden in TopBar.jsx — rendert dadurch NIE in Kiosk/TV/Packstation.
 * @since v2.660.0 (#1422)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Device } from '@twilio/voice-sdk';
import { Phone, PhoneOff, PhoneIncoming, PhoneForwarded, Mic, MicOff, Headphones, Circle, Pause, Play } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

const HEADSET_RE = /head|jabra|plantronics|poly|sennheiser|logitech|logi |usb|bluetooth|airpod/i;

function pickHeadsetDefault(devices) {
  for (const d of devices) {
    if (HEADSET_RE.test(d.label || '')) return d.deviceId;
  }
  return null; // kein Headset erkennbar → Browser-Default lassen, NIE Lautsprecher erzwingen
}

export default function SoftphoneWidget() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('offline'); // offline|connecting|ready|incoming|in-call|error
  const [presence, setPresence] = useState({ status: 'offline', manuell_gesetzt: false }); // #1424
  const [muted, setMuted] = useState(false);
  const [recState, setRecState] = useState('recording'); // #1428: recording|stopped (pro Call)
  const [callFrom, setCallFrom] = useState('');
  const [callerInfo, setCallerInfo] = useState(null); // #1455: {name, firma, quelle} aus DB-Lookup
  const [direktInfo, setDirektInfo] = useState(null); // #1464: '1' = Anrufer will zu DIR, 'v' = Vertretungs-Anruf
  const [callSeconds, setCallSeconds] = useState(0);
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [outputSupported, setOutputSupported] = useState(true);
  const [sel, setSel] = useState({ input_device_id: '', output_device_id: '' });
  const [errMsg, setErrMsg] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [dialValue, setDialValue] = useState(''); // #1433: interne Durchwahl
  const [dialBusy, setDialBusy] = useState(false);
  // #1429: Outbound — Echtzeit-Kontaktsuche + freie Nummer
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [outBusy, setOutBusy] = useState(false);
  const [outboundLabel, setOutboundLabel] = useState('');
  // #1457: Transfer warm/blind — Ziel-Auswahl + Warm-Zustand (überlebt den Hold-Disconnect)
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTargets, setTransferTargets] = useState({ kollegen: [], gruppen: [] });
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferExt, setTransferExt] = useState('');
  const [warmTransfer, setWarmTransfer] = useState(null); // {transfer_id, ziel_name}
  // #1458: Halten/Parken — Hold-Zustand überlebt den Disconnect (Muster warmTransfer)
  const [heldCall, setHeldCall] = useState(null); // {hold_id, von_nummer}
  const [holdBusy, setHoldBusy] = useState(false);
  const [parkedList, setParkedList] = useState([]);
  // #1456: MA↔MA Intern-Anruf — Kollegen-Präsenz-Liste + Anrufer-Name beim Angerufenen
  const [internInfo, setInternInfo] = useState(null); // Anrufer-NAME aus <Parameter name="intern">
  const [internTargetLabel, setInternTargetLabel] = useState(''); // Ziel-Name beim Anrufer
  const [kollegen, setKollegen] = useState([]);
  const [internBusy, setInternBusy] = useState(0); // user_id des laufenden Intern-Dials

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const pendingDialRef = useRef(0); // #1433: eigener Dial läuft → Leg 1 auto-annehmen
  const pendingOutboundRef = useRef(0); // #1429: eigener Outbound-Dial → Leg 1 auto-annehmen
  const panelRef = useRef(null);
  const selRef = useRef(sel);
  const timerRef = useRef(null);
  selRef.current = sel;

  // ─── Outside-Click schließt Panel ───
  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchToken = useCallback(async () => {
    const res = await fetch(`${API}/api/twilio/voice/token`, { credentials: 'include' });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
    return res.json();
  }, []);

  // ─── #1424 Präsenz: Events + Heartbeat-Poll (AK4: sichtbar ohne Reload) ───
  const presenceEvent = useCallback(async (typ) => {
    try {
      const res = await fetch(`${API}/api/voip/presence/event`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typ }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.me) setPresence(data.me);
    } catch { /* nächster Heartbeat korrigiert */ }
  }, []);

  const setPresenceManual = useCallback(async (newStatus) => {
    try {
      const res = await fetch(`${API}/api/voip/presence/me`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.me) setPresence(data.me);
    } catch { /* nächster Heartbeat korrigiert */ }
  }, []);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/api/voip/presence/heartbeat`, { method: 'POST', credentials: 'include' });
        const data = await res.json().catch(() => null);
        if (!stop && res.ok && data?.me) setPresence(data.me);
      } catch { /* offline → nächster Tick */ }
    };
    tick();
    const iv = setInterval(tick, 20000);
    return () => { stop = true; clearInterval(iv); };
  }, []);

  const refreshDeviceLists = useCallback(() => {
    const audio = deviceRef.current?.audio;
    if (!audio) return;
    setInputs([...audio.availableInputDevices.values()]);
    setOutputs([...audio.availableOutputDevices.values()]);
    setOutputSupported(!!audio.isOutputSelectionSupported);
  }, []);

  const applyDevices = useCallback(async (settings) => {
    const audio = deviceRef.current?.audio;
    if (!audio) return;
    try {
      if (settings.input_device_id && audio.availableInputDevices.has(settings.input_device_id)) {
        await audio.setInputDevice(settings.input_device_id);
      }
      if (audio.isOutputSelectionSupported && settings.output_device_id && audio.availableOutputDevices.has(settings.output_device_id)) {
        await audio.speakerDevices.set(settings.output_device_id);
        await audio.ringtoneDevices.set(settings.output_device_id);
      }
    } catch { /* Gerät nicht mehr verfügbar → Browser-Default */ }
  }, []);

  const persistSettings = useCallback(async (settings) => {
    try {
      await fetch(`${API}/api/twilio/voice/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings }),
      });
    } catch { /* nicht kritisch — nächster Save versucht es erneut */ }
  }, []);

  // ─── Verbindung + Device-Lifecycle (auto-connect beim Mount) ───
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setStatus('connecting');
        // Mic-Permission früh anfragen, damit Geräte-Labels sichtbar sind
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((tr) => tr.stop());
        } catch { /* User lehnt ab → Twilio fragt beim Call erneut */ }

        const [{ token }, settingsRes] = await Promise.all([
          fetchToken(),
          fetch(`${API}/api/twilio/voice/settings`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : { settings: {} })).catch(() => ({ settings: {} })),
        ]);
        if (cancelled) return;
        const saved = settingsRes.settings || {};

        const device = new Device(token, { logLevel: 'error', codecPreferences: ['opus', 'pcmu'] });
        deviceRef.current = device;

        device.on('registered', async () => {
          setStatus('ready');
          setErrMsg('');
          presenceEvent('register'); // #1424: offline + nicht manuell → available
          refreshDeviceLists();
          const audio = device.audio;
          const outs = [...audio.availableOutputDevices.values()];
          let outId = saved.output_device_id && audio.availableOutputDevices.has(saved.output_device_id)
            ? saved.output_device_id
            : pickHeadsetDefault(outs); // Großraum: Headset bevorzugen, nie Lautsprecher erzwingen
          let inId = saved.input_device_id && audio.availableInputDevices.has(saved.input_device_id)
            ? saved.input_device_id
            : pickHeadsetDefault([...audio.availableInputDevices.values()]);
          setSel({ input_device_id: inId || '', output_device_id: outId || '' });
          await applyDevices({ input_device_id: inId, output_device_id: outId });
        });
        device.on('error', (e) => { setErrMsg(e.message); setStatus('error'); });
        device.on('unregistered', () => setStatus('offline'));
        device.on('tokenWillExpire', async () => {
          try { device.updateToken((await fetchToken()).token); } catch { /* Renewal beim nächsten Tick */ }
        });
        device.audio?.on?.('deviceChange', refreshDeviceLists);

        device.on('incoming', (call) => {
          callRef.current = call;
          const fromParam = call.parameters?.From || '';
          setCallFrom(fromParam);
          // #1464: Direktanfrage-Kontext aus dem <Client><Parameter>-TwiML
          setDirektInfo(call.customParameters?.get?.('direkt') || null);
          // #1456: Intern-Anruf — Anrufer-NAME aus <Parameter name="intern"> (AK2);
          // From wäre die Hotline-CallerId, der Name übersteuert die Anzeige.
          const internName = call.customParameters?.get?.('intern') || null;
          setInternInfo(internName);
          // #1455: externe Nummer → Anrufer-ID-Lookup (Kunden→MA→Kontakte).
          // Fail-soft: kein Treffer/keine Permission → Rohnummer bleibt stehen.
          setCallerInfo(null);
          if (!internName && /^\+\d{6,15}$/.test(fromParam)) {
            fetch(`${API}/api/voip/caller-lookup?nummer=${encodeURIComponent(fromParam)}`, { credentials: 'include' })
              .then((r) => (r.ok ? r.json() : null))
              .then((d) => { if (d?.match && callRef.current === call) setCallerInfo(d.match); })
              .catch(() => { /* Rohnummer reicht */ });
          }
          setStatus('incoming');
          setOpen(true); // visuelle Anzeige: Panel öffnet sich, Icon pulsiert
          let accepted = false;
          call.on('accept', () => {
            accepted = true;
            setStatus('in-call');
            setMuted(call.isMuted());
            setCallSeconds(0);
            setRecState('recording'); // #1428: KI-Phase-Mitschnitt läuft default
            clearInterval(timerRef.current);
            timerRef.current = setInterval(() => setCallSeconds((s) => s + 1), 1000);
            presenceEvent('call_started'); // #1424 AK2: Call → busy
          });
          call.on('mute', (isMuted) => setMuted(isMuted));
          const cleanup = () => {
            callRef.current = null;
            clearInterval(timerRef.current);
            setMuted(false);
            setCallFrom('');
            setCallerInfo(null); // #1455: Anrufer-ID zurücksetzen
            setDirektInfo(null); // #1464: Direktanfrage-Kontext zurücksetzen
            setInternInfo(null); // #1456: Intern-Anrufer-Name zurücksetzen
            setInternTargetLabel('');
            setOutboundLabel(''); // #1429: Outbound-Anzeige zurücksetzen
            setRecState('recording'); // #1428: Reset fürs nächste Gespräch
            setTransferOpen(false); // #1457: Panel zu — warmTransfer überlebt den Hold-Disconnect bewusst
            setStatus('ready');
            if (accepted) presenceEvent('call_ended'); // #1424 AK2: zurück auf vorher
          };
          call.on('disconnect', cleanup);
          call.on('cancel', cleanup);
          call.on('reject', cleanup);
          // #1433: internes Wählen = REST-Zwei-Leg — Leg 1 klingelt das EIGENE
          // Widget (From=client:intern). Wurde der Dial gerade hier ausgelöst,
          // automatisch annehmen, damit sofort das Ziel klingelt.
          if (call.parameters?.From === 'client:intern' && Date.now() - pendingDialRef.current < 20000) {
            pendingDialRef.current = 0;
            call.accept();
          }
          // #1429: externer Outbound = gleiches Zwei-Leg-Muster (From=client:outbound)
          if (call.parameters?.From === 'client:outbound' && Date.now() - pendingOutboundRef.current < 20000) {
            pendingOutboundRef.current = 0;
            call.accept();
          }
        });

        await device.register();
      } catch (e) {
        if (!cancelled) { setErrMsg(e.message); setStatus('error'); }
      }
    })();

    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      try { deviceRef.current?.destroy(); } catch { /* bereits zerstört */ }
      deviceRef.current = null;
    };
  }, [fetchToken, refreshDeviceLists, applyDevices, presenceEvent]);

  // ─── Aktionen ───
  const accept = () => callRef.current?.accept();
  const reject = () => callRef.current?.reject();
  const hangup = () => callRef.current?.disconnect();
  // #1428 AK3: Anrufer-Widerspruch → Mitschnitt serverseitig beenden + bereits
  // Aufgenommenes verwerfen; das Gespräch selbst läuft unberührt weiter.
  const stopRecording = async () => {
    try {
      const res = await fetch(`${API}/api/voip/recording/stop`, { method: 'POST', credentials: 'include' });
      if (res.ok) setRecState('stopped');
    } catch { /* Button bleibt aktiv — erneut versuchen */ }
  };
  const toggleMute = () => {
    const call = callRef.current;
    if (!call) return;
    call.mute(!call.isMuted());
  };

  const changeDevice = async (kind, deviceId) => {
    const next = { ...selRef.current, [kind]: deviceId };
    setSel(next);
    await applyDevices(next);
    const labels = {};
    if (kind === 'input_device_id') labels.input_device_label = inputs.find((d) => d.deviceId === deviceId)?.label || '';
    if (kind === 'output_device_id') labels.output_device_label = outputs.find((d) => d.deviceId === deviceId)?.label || '';
    await persistSettings({ ...next, ...labels });
  };

  // #1433/#1445: interne Durchwahl wählen (2-stellig 10–99)
  const dialExtension = async () => {
    if (!/^[1-9]\d$/.test(dialValue)) return;
    setDialBusy(true);
    setErrMsg('');
    try {
      pendingDialRef.current = Date.now();
      const res = await fetch(`${API}/api/voip/extensions/dial`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ durchwahl: dialValue }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDialValue('');
    } catch (e) {
      pendingDialRef.current = 0;
      setErrMsg(e.message);
    } finally {
      setDialBusy(false);
    }
  };

  // ─── #1456: MA↔MA Intern-Anruf aus der Kollegen-Präsenz-Liste ───
  const dialInternal = async (userId, name) => {
    if (internBusy) return;
    setInternBusy(userId);
    setErrMsg('');
    try {
      pendingDialRef.current = Date.now();
      setInternTargetLabel(name || '');
      const res = await fetch(`${API}/api/voip/internal/dial`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // AK4: 409 mit grund (busy/pause/offline) → sauberer Hinweis statt Klingeln
        const statusLabel = data.grund && PRESENCE[data.grund] ? PRESENCE[data.grund].label : null;
        throw new Error(statusLabel
          ? `${name}: ${statusLabel} — ${t('softphone.internalUnavailable') || 'gerade nicht erreichbar'}`
          : (data.error || `HTTP ${res.status}`));
      }
    } catch (e) {
      pendingDialRef.current = 0;
      setInternTargetLabel('');
      setErrMsg(e.message);
    } finally {
      setInternBusy(0);
    }
  };

  // #1456: Kollegen-Liste — bei offenem Panel laden + alle 15s (Präsenz ändert sich laufend)
  useEffect(() => {
    if (!open) return undefined;
    let stop = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/voip/presence`, { credentials: 'include' });
        const data = await res.json().catch(() => null);
        if (!stop && res.ok && data) {
          setKollegen((data.presence || []).filter((p) => Number(p.user_id) !== Number(data.me_user_id)));
        }
      } catch { /* nächster Tick */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, [open]);

  // ─── #1429 Outbound: Kontaktsuche (Debounce 300 ms) + Wählen ───
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); setSearchBusy(false); return undefined; }
    setSearchBusy(true);
    let stale = false;
    const tm = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/voip/contacts/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (!stale) setSearchResults(res.ok ? (data.results || []) : []);
      } catch {
        if (!stale) setSearchResults([]);
      } finally {
        if (!stale) setSearchBusy(false);
      }
    }, 300);
    return () => { stale = true; clearTimeout(tm); };
  }, [searchQ]);

  // Freie-Nummer-Fallback: Eingabe clientseitig auf E.164 normalisieren (wie Server)
  const freeNumber = (() => {
    let n = searchQ.replace(/[\s\-()/.]/g, '');
    if (!n) return null;
    if (n.startsWith('00')) n = `+${n.slice(2)}`;
    else if (n.startsWith('0')) n = `+43${n.slice(1)}`;
    return /^\+\d{6,15}$/.test(n) ? n : null;
  })();

  const dialOutbound = async (nummer, { name, quelle, kundeId } = {}) => {
    setOutBusy(true);
    setErrMsg('');
    try {
      pendingOutboundRef.current = Date.now();
      setOutboundLabel(name ? `${name} (${nummer})` : nummer);
      const res = await fetch(`${API}/api/voip/outbound/dial`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nummer, kontakt_name: name || null, kontakt_quelle: quelle || 'frei', kunde_id: kundeId || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSearchQ('');
      setSearchResults([]);
    } catch (e) {
      pendingOutboundRef.current = 0;
      setOutboundLabel('');
      setErrMsg(e.message);
    } finally {
      setOutBusy(false);
    }
  };

  // ─── #1457 Transfer warm/blind ───
  const toggleTransfer = () => {
    const next = !transferOpen;
    setTransferOpen(next);
    if (!next) return;
    // Ziele bei jedem Öffnen frisch laden (Präsenz ändert sich laufend)
    fetch(`${API}/api/voip/transfer/targets`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTransferTargets({ kollegen: d.kollegen || [], gruppen: d.gruppen || [] }); })
      .catch(() => { /* Liste bleibt leer — erneut öffnen */ });
  };

  const doTransfer = async (mode, ziel, name) => {
    const sid = callRef.current?.parameters?.CallSid;
    if (!sid || transferBusy) return;
    setTransferBusy(true);
    setErrMsg('');
    try {
      // Warm: der Ankündigungs-Call klingelt gleich als client:intern → auto-annehmen
      if (mode === 'warm') pendingDialRef.current = Date.now();
      const res = await fetch(`${API}/api/voip/transfer/${mode === 'warm' ? 'warm/start' : 'blind'}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_sid: sid, ziel }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (mode === 'warm') setWarmTransfer({ transfer_id: data.transfer_id, ziel_name: data.ziel_name || name || '' });
      setTransferOpen(false);
      setTransferExt('');
    } catch (e) {
      if (mode === 'warm') pendingDialRef.current = 0;
      setErrMsg(e.message);
    } finally {
      setTransferBusy(false);
    }
  };

  const warmAction = async (action) => {
    if (!warmTransfer || transferBusy) return;
    setTransferBusy(true);
    setErrMsg('');
    try {
      const res = await fetch(`${API}/api/voip/transfer/warm/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transfer_id: warmTransfer.transfer_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setWarmTransfer(null);
    } catch (e) {
      setErrMsg(e.message);
    } finally {
      setTransferBusy(false);
    }
  };

  // ─── #1458 Halten/Parken ───
  const doHold = async (mode) => {
    const sid = callRef.current?.parameters?.CallSid;
    if (!sid || holdBusy) return;
    setHoldBusy(true);
    setErrMsg('');
    try {
      const res = await fetch(`${API}/api/voip/${mode}/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_sid: sid }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (mode === 'hold') setHeldCall({ hold_id: data.hold_id, von_nummer: data.von_nummer || callFrom });
      // Park: Anruf landet in der zentralen Liste — der Poll zeigt ihn allen MAs
    } catch (e) {
      setErrMsg(e.message);
    } finally {
      setHoldBusy(false);
    }
  };

  const resumeHold = async () => {
    if (!heldCall || holdBusy) return;
    setHoldBusy(true);
    setErrMsg('');
    try {
      // Join-Call kommt gleich als client:intern → auto-annehmen
      pendingDialRef.current = Date.now();
      const res = await fetch(`${API}/api/voip/hold/resume`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hold_id: heldCall.hold_id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 404/409 = Hold weg bzw. Anrufer aufgelegt → Panel schließen
        if (res.status === 404 || res.status === 409) setHeldCall(null);
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setHeldCall(null);
    } catch (e) {
      pendingDialRef.current = 0;
      setErrMsg(e.message);
    } finally {
      setHoldBusy(false);
    }
  };

  const pickupParked = async (parkId) => {
    if (holdBusy) return;
    setHoldBusy(true);
    setErrMsg('');
    try {
      pendingDialRef.current = Date.now();
      const res = await fetch(`${API}/api/voip/park/pickup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ park_id: parkId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setParkedList((l) => l.filter((p) => p.park_id !== parkId));
    } catch (e) {
      pendingDialRef.current = 0;
      setErrMsg(e.message);
      // Liste sofort korrigieren (Anruf evtl. weg/übernommen)
      fetch(`${API}/api/voip/park/list`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) setParkedList(d.parked || []); })
        .catch(() => {});
    } finally {
      setHoldBusy(false);
    }
  };

  // Park-Liste: bei offenem Panel laden + alle 15s aktualisieren (zentral für alle MAs)
  useEffect(() => {
    if (!open) return undefined;
    let stop = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/voip/park/list`, { credentials: 'include' });
        const data = await res.json().catch(() => null);
        if (!stop && res.ok && data) setParkedList(data.parked || []);
      } catch { /* nächster Tick */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, [open]);

  const triggerTestCall = async () => {
    setTestBusy(true);
    try {
      const res = await fetch(`${API}/api/twilio/voice/test-call`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    } catch (e) {
      setErrMsg(e.message);
    } finally {
      setTestBusy(false);
    }
  };

  const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // #1424: Präsenz-Farben (Status-Badges dürfen farbig sein)
  const PRESENCE = {
    available: { label: t('softphone.presenceAvailable') || 'Verfügbar', color: '#16A34A' },
    busy: { label: t('softphone.presenceBusy') || 'Im Gespräch', color: '#DC2626' },
    pause: { label: t('softphone.presencePause') || 'Pause', color: '#D97706' },
    offline: { label: t('softphone.offline') || 'Offline', color: '#9CA3AF' },
  };

  const statusInfo = {
    offline: { label: t('softphone.offline') || 'Offline', color: '#9CA3AF' },
    connecting: { label: t('softphone.connecting') || 'Verbinde …', color: '#9CA3AF' },
    // Bereit → Punkt + Label zeigen die PRÄSENZ (available/pause/offline)
    ready: PRESENCE[presence.status] || PRESENCE.offline,
    incoming: { label: t('softphone.incoming') || 'Eingehender Anruf', color: '#338edc' },
    'in-call': PRESENCE.busy,
    error: { label: t('softphone.error') || 'Fehler', color: '#DC2626' },
  }[status];

  const isIncoming = status === 'incoming';
  const inCall = status === 'in-call';

  // #1455: Anrufer-Anzeige — Name (Firma) aus DB-Lookup, sonst Rohnummer.
  // #1456: Intern-Anruf — Anrufer-NAME übersteuert die Hotline-CallerId (AK2);
  // beim Anrufer zeigt das eigene Leg (From=client:intern) den Ziel-Namen.
  const callerLabel = internInfo
    ? `${internInfo} (${t('softphone.internalLabel') || 'intern'})`
    : callFrom === 'client:intern'
    ? (internTargetLabel ? `${internTargetLabel} (${t('softphone.internalLabel') || 'intern'})` : (t('softphone.internalCall') || 'Interner Anruf'))
    : callFrom === 'client:outbound'
      ? (outboundLabel || (t('softphone.outboundCall') || 'Ausgehender Anruf'))
      : callerInfo
        ? `${callerInfo.name}${callerInfo.firma ? ` · ${callerInfo.firma}` : ''} (${callFrom})`
        : (callFrom || '—');

  const selectCls = 'w-full text-xs rounded-[8px] border border-line bg-surface-input text-[var(--text-primary)] px-2 py-1.5 focus:outline-none focus:border-[#338edc]';

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={t('softphone.tooltip') || 'Softphone'}
        className={`relative w-9 h-9 flex items-center justify-center rounded-[10px] border border-line bg-surface-input text-[var(--text-secondary)] hover:text-content-secondary hover:bg-surface-hover transition-colors ${isIncoming ? 'animate-pulse' : ''}`}
        style={isIncoming ? { borderColor: '#338edc', color: '#338edc' } : inCall ? { color: '#16A34A' } : undefined}
      >
        {isIncoming ? <PhoneIncoming size={16} /> : <Phone size={16} />}
        <span
          className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[var(--bg-surface,#fff)] ${isIncoming ? 'animate-ping' : ''}`}
          style={{ background: statusInfo.color }}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-surface-elevated border border-line-hover rounded-card shadow-elevated z-50 overflow-hidden">
          {/* Header: Status */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-line">
            <span className="text-xs font-semibold text-content-secondary flex items-center gap-2">
              <Headphones size={13} />
              {t('softphone.title') || 'Softphone'}
            </span>
            <span className="text-[10px] font-medium flex items-center gap-1.5" style={{ color: statusInfo.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusInfo.color }} />
              {statusInfo.label}
              {inCall && <span className="font-mono text-[var(--text-tertiary)]">{fmtDur(callSeconds)}</span>}
            </span>
          </div>

          {/* #1424 Mein Status — klickbar (available/pause/offline) */}
          {!isIncoming && !inCall && (
            <div className="px-3 py-2.5 border-b border-line">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                {t('softphone.presenceTitle') || 'Mein Status'}
              </div>
              <div className="flex gap-1.5">
                {['available', 'pause', 'offline'].map((s) => {
                  const active = presence.status === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setPresenceManual(s)}
                      className={`flex-1 px-2 py-1.5 rounded-[8px] text-[11px] font-medium border transition-colors flex items-center justify-center gap-1.5 ${active ? 'bg-white shadow-sm' : 'bg-[var(--bg-inset,#F8F9FC)]'}`}
                      style={{ borderColor: active ? PRESENCE[s].color : '#E5E7EB', color: active ? PRESENCE[s].color : 'var(--text-tertiary, #6B7280)' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: PRESENCE[s].color }} />
                      {PRESENCE[s].label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* #1429: Outbound — Kontaktsuche (Kunden/Mitarbeiter/Kontakte) + freie Nummer */}
          {status === 'ready' && (
            <div className="px-3 py-2.5 border-b border-line">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                {t('softphone.searchTitle') || 'Anrufen'}
              </div>
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && freeNumber && !outBusy) dialOutbound(freeNumber, { quelle: 'frei' }); }}
                placeholder={t('softphone.searchPlaceholder') || 'Name oder Nummer …'}
                className={`w-full ${selectCls}`}
                disabled={outBusy}
              />
              {searchBusy && (
                <div className="mt-1.5 text-[11px] text-[var(--text-muted)]">{t('softphone.searching') || 'Suche …'}</div>
              )}
              {!searchBusy && searchQ.trim().length >= 2 && searchResults.length === 0 && !freeNumber && (
                <div className="mt-1.5 text-[11px] text-[var(--text-muted)]">{t('softphone.noResults') || 'Keine Treffer'}</div>
              )}
              {searchResults.length > 0 && (
                <div className="mt-1.5 max-h-48 overflow-y-auto rounded-[8px] border border-line divide-y divide-[var(--line,#E5E7EB)]">
                  {searchResults.map((r, ri) => (
                    <div key={`${r.quelle}-${r.id}-${ri}`} className="px-2 py-1.5">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-xs font-medium text-[var(--text-primary)] truncate">{r.name}</span>
                        <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border"
                          style={r.quelle === 'kunde'
                            ? { color: '#338edc', borderColor: '#338edc' }
                            : r.quelle === 'mitarbeiter'
                              ? { color: '#16A34A', borderColor: '#16A34A' }
                              : { color: '#6366F1', borderColor: '#6366F1' }}
                        >
                          {r.quelle === 'kunde' ? (t('softphone.sourceKunde') || 'Kunde')
                            : r.quelle === 'mitarbeiter' ? (t('softphone.sourceMitarbeiter') || 'Mitarbeiter')
                              : (t('softphone.sourceKontakt') || 'Kontakt')}
                        </span>
                      </div>
                      {r.firma && <div className="text-[10px] text-[var(--text-tertiary)] truncate mb-0.5">{r.firma}</div>}
                      {(r.nummern || []).map((n, ni) => (
                        <button
                          key={`${n.nummer}-${ni}`}
                          onClick={() => dialOutbound(n.nummer, { name: r.name, quelle: r.quelle, kundeId: r.kunde_id })}
                          disabled={outBusy}
                          className="w-full flex items-center gap-1.5 px-1.5 py-1 rounded-[6px] text-[11px] font-mono text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40 text-left"
                        >
                          <Phone size={11} style={{ color: '#338edc' }} />
                          {n.nummer}
                          {n.label && <span className="font-sans text-[10px] text-[var(--text-muted)]">({n.label})</span>}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {freeNumber && (
                <button
                  onClick={() => dialOutbound(freeNumber, { quelle: 'frei' })}
                  disabled={outBusy}
                  className="mt-1.5 w-full px-3 py-2 rounded-[8px] text-white text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40"
                  style={{ background: '#338edc' }}
                >
                  <Phone size={13} />
                  {outBusy ? (t('softphone.dialBusy') || 'Wähle …') : `${t('softphone.callFreeNumber') || 'Anrufen'}: ${freeNumber}`}
                </button>
              )}
            </div>
          )}

          {/* #1433: Interne Durchwahl wählen (MA↔MA) */}
          {status === 'ready' && (
            <div className="px-3 py-2.5 border-b border-line">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                {t('softphone.dialExtension') || 'Durchwahl wählen'}
              </div>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={dialValue}
                  onChange={(e) => setDialValue(e.target.value.replace(/\D/g, '').slice(0, 2))}
                  onKeyDown={(e) => { if (e.key === 'Enter') dialExtension(); }}
                  placeholder={t('softphone.dialPlaceholder') || 'z.B. 12'}
                  className={`flex-1 ${selectCls} font-mono`}
                />
                <button
                  onClick={dialExtension}
                  disabled={!/^[1-9]\d$/.test(dialValue) || dialBusy}
                  className="px-3 py-1.5 rounded-[8px] text-white text-xs font-medium flex items-center gap-1.5 disabled:opacity-40"
                  style={{ background: '#338edc' }}
                >
                  <Phone size={13} />
                  {dialBusy ? (t('softphone.dialBusy') || 'Wähle …') : (t('softphone.dialCall') || 'Anrufen')}
                </button>
              </div>
            </div>
          )}

          {/* #1456: Kollegen — Präsenz-Liste mit Intern-Anruf (MA↔MA) */}
          {status === 'ready' && kollegen.length > 0 && (
            <div className="px-3 py-2.5 border-b border-line">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                {t('softphone.colleaguesTitle') || 'Kollegen'}
              </div>
              <div className="max-h-48 overflow-y-auto rounded-[8px] border border-line divide-y divide-[var(--line,#E5E7EB)]">
                {kollegen.map((k) => {
                  const p = PRESENCE[k.status] || PRESENCE.offline;
                  return (
                    <div key={k.user_id} className="px-2 py-1.5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: p.color }} />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[11px] font-medium text-[var(--text-primary)] truncate">{k.name}</span>
                        <span className="block text-[10px] text-[var(--text-tertiary)] truncate">{p.label}</span>
                      </span>
                      {k.durchstellbar ? (
                        <button
                          onClick={() => dialInternal(k.user_id, k.name)}
                          disabled={!!internBusy}
                          className="px-2 py-1 rounded-[6px] text-[10px] font-medium text-white disabled:opacity-40 flex items-center gap-1"
                          style={{ background: '#338edc' }}
                        >
                          <Phone size={10} />
                          {internBusy === k.user_id ? (t('softphone.dialBusy') || 'Wähle …') : (t('softphone.dialCall') || 'Anrufen')}
                        </button>
                      ) : (
                        <span className="text-[10px] text-[var(--text-muted)]">{t('softphone.internalUnavailableShort') || 'nicht erreichbar'}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* #1458: Gehaltener Anruf — Anrufer hört Wartemusik, nur ich hole zurück */}
          {heldCall && (
            <div className="px-3 py-3 border-b border-line" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
              <div className="text-xs text-[var(--text-secondary)] mb-2 flex items-center gap-1.5">
                <Pause size={13} style={{ color: '#D97706' }} />
                {(t('softphone.holdActive') || 'Anruf gehalten')}{heldCall.von_nummer ? <> — <span className="font-medium text-[var(--text-primary)]">{heldCall.von_nummer}</span></> : null}
              </div>
              <button
                onClick={resumeHold}
                disabled={holdBusy || inCall || isIncoming}
                className="w-full px-3 py-2 rounded-[8px] text-white text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40"
                style={{ background: '#16A34A' }}
              >
                <Play size={13} /> {t('softphone.holdResume') || 'Fortsetzen'}
              </button>
            </div>
          )}

          {/* #1458: Geparkte Anrufe — zentral, jeder MA darf übernehmen (AK2) */}
          {status === 'ready' && parkedList.length > 0 && (
            <div className="px-3 py-2.5 border-b border-line">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                {t('softphone.parkedTitle') || 'Geparkte Anrufe'}
              </div>
              <div className="rounded-[8px] border border-line divide-y divide-[var(--line,#E5E7EB)]">
                {parkedList.map((p) => (
                  <div key={p.park_id} className="px-2 py-1.5 flex items-center gap-1.5">
                    <Pause size={11} className="shrink-0" style={{ color: '#D97706' }} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-[11px] font-mono text-[var(--text-primary)] truncate">{p.von_nummer}</span>
                      <span className="block text-[10px] text-[var(--text-tertiary)] truncate">
                        {(t('softphone.parkedBy') || 'geparkt von')} {p.geparkt_von} · {fmtDur(p.seit_sek || 0)}
                      </span>
                    </span>
                    <button
                      onClick={() => pickupParked(p.park_id)}
                      disabled={holdBusy}
                      className="px-2 py-1 rounded-[6px] text-[10px] font-medium text-white disabled:opacity-40"
                      style={{ background: '#338edc' }}
                    >
                      {t('softphone.parkPickup') || 'Übernehmen'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* #1457: Warm-Transfer aktiv — Anrufer wartet auf Hold (AK2/AK4) */}
          {warmTransfer && (
            <div className="px-3 py-3 border-b border-line" style={{ background: 'var(--bg-inset, #F8F9FC)' }}>
              <div className="text-xs text-[var(--text-secondary)] mb-2 flex items-center gap-1.5">
                <PhoneForwarded size={13} style={{ color: '#338edc' }} />
                {(t('softphone.transferOnHold') || 'Anrufer wartet (Hold)')}{warmTransfer.ziel_name ? <> — <span className="font-medium text-[var(--text-primary)]">{warmTransfer.ziel_name}</span></> : null}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => warmAction('complete')}
                  disabled={transferBusy}
                  className="flex-1 px-3 py-2 rounded-[8px] text-white text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-40"
                  style={{ background: '#16A34A' }}
                >
                  <Phone size={13} /> {t('softphone.transferConnect') || 'Verbinden'}
                </button>
                <button
                  onClick={() => warmAction('cancel')}
                  disabled={transferBusy}
                  className="flex-1 px-3 py-2 rounded-[8px] text-xs font-medium flex items-center justify-center gap-1.5 border disabled:opacity-40"
                  style={{ background: '#fff', borderColor: '#DC2626', color: '#DC2626' }}
                >
                  <PhoneOff size={13} /> {t('softphone.transferRetrieve') || 'Zurückholen'}
                </button>
              </div>
            </div>
          )}

          {/* Eingehender Anruf */}
          {isIncoming && (
            <div className="px-3 py-3 border-b border-line">
              <div className="text-xs text-[var(--text-secondary)] mb-2">
                {(t('softphone.callFrom') || 'Anruf von')} <span className="font-medium text-[var(--text-primary)]">{callerLabel}</span>
              </div>
              {/* #1464: Direktanfrage-Kontext — Anrufer hat explizit nach dir bzw. einem Kollegen (Vertretung) gefragt */}
              {direktInfo === '1' && (
                <div className="text-xs font-medium mb-2" style={{ color: '#338edc' }}>
                  {t('softphone.directRequest') || 'Möchte zu Ihnen durchgestellt werden'}
                </div>
              )}
              {direktInfo === 'v' && (
                <div className="text-xs font-medium mb-2" style={{ color: '#338edc' }}>
                  {t('softphone.directRequestSubstitute') || 'Vertretungs-Anruf — der gewünschte Kollege ist nicht erreichbar'}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={accept} className="flex-1 px-3 py-2 rounded-[8px] text-white text-xs font-medium flex items-center justify-center gap-1.5" style={{ background: '#16A34A' }}>
                  <Phone size={13} /> {t('softphone.accept') || 'Annehmen'}
                </button>
                <button onClick={reject} className="flex-1 px-3 py-2 rounded-[8px] text-white text-xs font-medium flex items-center justify-center gap-1.5" style={{ background: '#DC2626' }}>
                  <PhoneOff size={13} /> {t('softphone.reject') || 'Ablehnen'}
                </button>
              </div>
            </div>
          )}

          {/* Im Gespräch: Mute PROMINENT + Auflegen */}
          {inCall && (
            <div className="px-3 py-3 border-b border-line">
              <div className="text-xs text-[var(--text-secondary)] mb-2">
                {callFrom && <>{(t('softphone.connectedWith') || 'Verbunden mit')} <span className="font-medium text-[var(--text-primary)]">{callerLabel}</span></>}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={toggleMute}
                  className="flex-1 px-3 py-2.5 rounded-[8px] text-xs font-semibold flex items-center justify-center gap-1.5 border transition-colors"
                  style={muted
                    ? { background: '#DC2626', borderColor: '#DC2626', color: '#fff' }
                    : { background: 'var(--bg-inset, #F8F9FC)', borderColor: '#E5E7EB', color: 'var(--text-primary, #1A1A2E)' }}
                >
                  {muted ? <MicOff size={14} /> : <Mic size={14} />}
                  {muted ? (t('softphone.unmute') || 'Stumm — aufheben') : (t('softphone.mute') || 'Stumm schalten')}
                </button>
                <button onClick={hangup} className="px-3 py-2.5 rounded-[8px] text-white text-xs font-medium flex items-center justify-center gap-1.5" style={{ background: '#DC2626' }}>
                  <PhoneOff size={13} /> {t('softphone.hangup') || 'Auflegen'}
                </button>
              </div>
              {/* #1458: Halten/Parken — versteckt während Warm-Transfer (AK3 konfliktfrei) */}
              {!warmTransfer && (
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => doHold('hold')}
                    disabled={holdBusy}
                    className="flex-1 px-3 py-2 rounded-[8px] text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors disabled:opacity-40"
                    style={{ background: '#fff', borderColor: '#D97706', color: '#D97706' }}
                  >
                    <Pause size={13} /> {t('softphone.hold') || 'Halten'}
                  </button>
                  <button
                    onClick={() => doHold('park')}
                    disabled={holdBusy}
                    className="flex-1 px-3 py-2 rounded-[8px] text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors disabled:opacity-40"
                    style={{ background: '#fff', borderColor: '#6366F1', color: '#6366F1' }}
                  >
                    <PhoneForwarded size={13} /> {t('softphone.park') || 'Parken'}
                  </button>
                </div>
              )}
              {/* #1457: Weiterleiten — Ziel-Auswahl Kollege/Gruppe/Durchwahl, blind/warm (AK1–AK3) */}
              {!warmTransfer && (
                <button
                  onClick={toggleTransfer}
                  className="mt-2 w-full px-3 py-2 rounded-[8px] text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors"
                  style={transferOpen
                    ? { background: '#338edc', borderColor: '#338edc', color: '#fff' }
                    : { background: '#fff', borderColor: '#338edc', color: '#338edc' }}
                >
                  <PhoneForwarded size={13} />
                  {t('softphone.transferTitle') || 'Weiterleiten'}
                </button>
              )}
              {transferOpen && !warmTransfer && (
                <div className="mt-2 rounded-[8px] border border-line overflow-hidden">
                  <div className="max-h-48 overflow-y-auto divide-y divide-[var(--line,#E5E7EB)]">
                    {transferTargets.kollegen.filter((k) => k.durchstellbar).map((k) => (
                      <div key={`u-${k.user_id}`} className="px-2 py-1.5 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#16A34A' }} />
                        <span className="flex-1 text-[11px] text-[var(--text-primary)] truncate">{k.name}</span>
                        <button onClick={() => doTransfer('blind', { typ: 'user', id: k.user_id }, k.name)} disabled={transferBusy}
                          className="px-2 py-1 rounded-[6px] text-[10px] font-medium text-white disabled:opacity-40" style={{ background: '#338edc' }}>
                          {t('softphone.transferBlind') || 'Direkt'}
                        </button>
                        <button onClick={() => doTransfer('warm', { typ: 'user', id: k.user_id }, k.name)} disabled={transferBusy}
                          className="px-2 py-1 rounded-[6px] text-[10px] font-medium border disabled:opacity-40" style={{ borderColor: '#338edc', color: '#338edc' }}>
                          {t('softphone.transferWarm') || 'Ankündigen'}
                        </button>
                      </div>
                    ))}
                    {transferTargets.gruppen.map((g) => (
                      <div key={`g-${g.id}`} className="px-2 py-1.5 flex items-center gap-1.5">
                        <span className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full border shrink-0" style={{ color: '#6366F1', borderColor: '#6366F1' }}>
                          {t('softphone.transferGroup') || 'Gruppe'}
                        </span>
                        <span className="flex-1 text-[11px] text-[var(--text-primary)] truncate">{g.name}</span>
                        <button onClick={() => doTransfer('blind', { typ: 'group', id: g.id }, g.name)} disabled={transferBusy}
                          className="px-2 py-1 rounded-[6px] text-[10px] font-medium text-white disabled:opacity-40" style={{ background: '#338edc' }}>
                          {t('softphone.transferBlind') || 'Direkt'}
                        </button>
                        <button onClick={() => doTransfer('warm', { typ: 'group', id: g.id }, g.name)} disabled={transferBusy}
                          className="px-2 py-1 rounded-[6px] text-[10px] font-medium border disabled:opacity-40" style={{ borderColor: '#338edc', color: '#338edc' }}>
                          {t('softphone.transferWarm') || 'Ankündigen'}
                        </button>
                      </div>
                    ))}
                    {transferTargets.kollegen.filter((k) => k.durchstellbar).length === 0 && transferTargets.gruppen.length === 0 && (
                      <div className="px-2 py-2 text-[11px] text-[var(--text-muted)]">{t('softphone.transferNoTargets') || 'Keine durchstellbaren Ziele'}</div>
                    )}
                  </div>
                  <div className="px-2 py-1.5 border-t border-line flex items-center gap-1.5">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={2}
                      value={transferExt}
                      onChange={(e) => setTransferExt(e.target.value.replace(/\D/g, '').slice(0, 2))}
                      placeholder={t('softphone.dialPlaceholder') || 'z.B. 12'}
                      className={`w-16 ${selectCls} font-mono`}
                    />
                    <button onClick={() => doTransfer('blind', { typ: 'extension', id: transferExt })} disabled={!/^[1-9]\d$/.test(transferExt) || transferBusy}
                      className="flex-1 px-2 py-1.5 rounded-[6px] text-[10px] font-medium text-white disabled:opacity-40" style={{ background: '#338edc' }}>
                      {t('softphone.transferBlind') || 'Direkt'}
                    </button>
                    <button onClick={() => doTransfer('warm', { typ: 'extension', id: transferExt })} disabled={!/^[1-9]\d$/.test(transferExt) || transferBusy}
                      className="flex-1 px-2 py-1.5 rounded-[6px] text-[10px] font-medium border disabled:opacity-40" style={{ borderColor: '#338edc', color: '#338edc' }}>
                      {t('softphone.transferWarm') || 'Ankündigen'}
                    </button>
                  </div>
                </div>
              )}
              {/* #1428: Anrufer-Widerspruch → MA stoppt+verwirft den Mitschnitt (AK3) */}
              <button
                onClick={stopRecording}
                disabled={recState === 'stopped'}
                className="mt-2 w-full px-3 py-2 rounded-[8px] text-xs font-medium flex items-center justify-center gap-1.5 border transition-colors"
                style={recState === 'stopped'
                  ? { background: 'var(--bg-inset, #F8F9FC)', borderColor: '#E5E7EB', color: 'var(--text-muted, #9CA3AF)' }
                  : { background: '#fff', borderColor: '#DC2626', color: '#DC2626' }}
              >
                <Circle size={11} fill={recState === 'stopped' ? 'none' : 'currentColor'} />
                {recState === 'stopped'
                  ? (t('softphone.recordingStopped') || 'Aufnahme verworfen')
                  : (t('softphone.stopRecording') || 'Aufnahme stoppen & verwerfen')}
              </button>
            </div>
          )}

          {/* Geräte-Wahl — prominent (Großraum: Headset!) */}
          <div className="px-3 py-3 space-y-2.5">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                {t('softphone.microphone') || 'Mikrofon'}
              </label>
              <select value={sel.input_device_id} onChange={(e) => changeDevice('input_device_id', e.target.value)} className={selectCls}>
                <option value="">{t('softphone.systemDefault') || 'System-Standard'}</option>
                {inputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">
                {t('softphone.output') || 'Ausgabe (Kopfhörer)'}
              </label>
              {outputSupported ? (
                <select value={sel.output_device_id} onChange={(e) => changeDevice('output_device_id', e.target.value)} className={selectCls}>
                  <option value="">{t('softphone.systemDefault') || 'System-Standard'}</option>
                  {outputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || d.deviceId}</option>)}
                </select>
              ) : (
                <div className="text-[10px] text-[var(--text-tertiary)]">{t('softphone.outputUnsupported') || 'Ausgabe-Wahl wird von diesem Browser nicht unterstützt (Chrome/Edge verwenden).'}</div>
              )}
              <div className="text-[10px] text-[var(--text-tertiary)] mt-1">
                {t('softphone.headsetHint') || 'Großraumbüro: bitte Headset verwenden.'}
              </div>
            </div>
            {errMsg && <div className="text-[10px] text-red-400">{errMsg}</div>}
          </div>

          {/* Footer */}
          <div className="border-t border-line px-3 py-2 flex items-center justify-between">
            <button
              onClick={triggerTestCall}
              disabled={status !== 'ready' || testBusy}
              className="text-[10px] text-primary-400 hover:text-primary-300 font-medium disabled:opacity-40"
            >
              {testBusy ? (t('softphone.testCallBusy') || 'Test-Anruf läuft …') : (t('softphone.testCall') || 'Test-Anruf')}
            </button>
            <span className="text-[9px] text-[var(--text-muted)]">{t('softphone.stage') || 'Softphone v2b'}</span>
          </div>
        </div>
      )}
    </div>
  );
}
